// Blokkauspatternit: YKSI jaettu fallback-lista (V2-T11, PL-22).
//
// Vaiheissa 0-1 inline-blokkerilla ja pääskriptillä oli kaksi eri listaa:
// blokkerin lista oli suppeampi, joten sama seurantaskripti saattoi estyä
// tai olla estymättä sen mukaan, kummalla mekanismilla se osui. Ero oli
// peritty vaihe 0:n toteutuksesta (PL-7) ja säilytettiin vaiheessa 1
// tietoisesti, koska V1 ei saanut muuttaa käyttäytymistä.
//
// Nyt lista on yksi ja sama molemmille. Yhtenäistäminen tehtiin
// LAAJEMMAN listan ehdoilla: yhdistetty lista on kahden vanhan listan
// yhdiste, joten estokattavuus vain kasvaa eikä kapene millään polulla.
// Yhdisteen todistaa yksikkötesti, jossa vanhat listat ovat kiinteinä
// vertailuarvoina (patterns.test.ts).

import type { BlockMatch, BlockPatterns, Choices } from './types';
import { patternsKey } from './storage';

/**
 * Jaettu fallback-patternilista: sama inline-blokkerille ja pääskriptille.
 * Käytössä vain siihen asti, kunnes sivuston oma konfiguraatio (evästeiden
 * tarjoajat) on ladattu tai luettu localStorage-välimuistista.
 */
export const FALLBACK_PATTERNS: BlockPatterns = {
  analytics: [
    'google-analytics.com',
    'googletagmanager.com',
    'analytics.google.com',
    'plausible.io',
    'hotjar.com',
    'clarity.ms',
  ],
  marketing: [
    'connect.facebook.net',
    'ads.linkedin.com',
    'snap.licdn.com',
    'doubleclick.net',
    'googlesyndication.com',
    'googleadservices.com',
    'fbevents.js',
  ],
  social: ['platform.twitter.com', 'platform.linkedin.com'],
  preferences: ['intercomcdn.com', 'crisp.chat'],
};

/**
 * Vanhat nimet osoittavat samaan listaan (V2-T11). Säilytetään, jotta paketin
 * julkinen rajapinta ei rikkoudu kesken vaiheen — poistetaan vaiheessa 3.
 * @deprecated käytä FALLBACK_PATTERNS
 */
export const BLOCKER_FALLBACK_PATTERNS = FALLBACK_PATTERNS;
/** @deprecated käytä FALLBACK_PATTERNS */
export const MAIN_FALLBACK_PATTERNS = FALLBACK_PATTERNS;

/** Kopioi patternit uudeksi objektiksi (taulukot slice-kopioina). */
export function clonePatterns(source: BlockPatterns): BlockPatterns {
  const target: BlockPatterns = {};
  for (const k in source) target[k] = source[k].slice();
  return target;
}

/** Yhdistää localStorage-välimuistin patternit (inline-blokkerin merge). */
export function mergeCachedPatterns(target: BlockPatterns, cached: BlockPatterns | null | undefined): void {
  if (!cached) return;
  for (const s in cached) {
    if (!target[s]) target[s] = [];
    for (let j = 0; j < cached[s].length; j++) {
      if (target[s].indexOf(cached[s][j]) === -1) target[s].push(cached[s][j]);
    }
  }
}

/** Lukee patternivälimuistin localStoragesta (tai null). */
export function readCachedPatterns(domain: string): BlockPatterns | null {
  try {
    return JSON.parse(localStorage.getItem(patternsKey(domain)) as string);
  } catch {
    return null;
  }
}

/**
 * Pitääkö src estää annetuilla valinnoilla. Välttämättömät kategoriat
 * ('essential', 'valttamattomat') ohitetaan aina; hyväksytyt kategoriat
 * ohitetaan; osuma on case-insensitiivinen substring-vertailu.
 */
export function shouldBlockSrc(
  patterns: BlockPatterns,
  src: string | null | undefined,
  consent: Choices | null | undefined,
): BlockMatch | false {
  if (!src) return false;
  const s = src.toLowerCase();
  for (const slug in patterns) {
    if (slug === 'essential' || slug === 'valttamattomat') continue;
    if (consent && consent[slug]) continue;
    const list = patterns[slug];
    for (let i = 0; i < list.length; i++) {
      if (s.indexOf(list[i].toLowerCase()) !== -1) return { slug, pattern: list[i] };
    }
  }
  return false;
}
